// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { QuestionnaireResponse } from '@medplum/fhirtypes';

export interface TestIdentity {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  timestamp: number;
}

export function generateTestIdentity(): TestIdentity {
  const ts = Date.now();
  return {
    firstName: 'Zoom',
    lastName: `Test${ts}`,
    email: `zoom-test-${ts}@polaristest.com`,
    phone: '5555550100',
    dateOfBirth: '1985-06-15',
    timestamp: ts,
  };
}

/**
 * Minimal-valid self-referral intake QR. Self-pay path so no insurance is required.
 * Matches the linkIds expected by `buildPatientResource` and the insurance/consent
 * checks in `processIntakeForm`.
 */
export function buildIntakeQuestionnaireResponse(identity: TestIdentity): QuestionnaireResponse {
  return {
    resourceType: 'QuestionnaireResponse',
    questionnaire: 'Questionnaire/self-referral-intake-form',
    status: 'completed',
    authored: new Date().toISOString(),
    item: [
      { linkId: 'first-name', answer: [{ valueString: identity.firstName }] },
      { linkId: 'last-name', answer: [{ valueString: identity.lastName }] },
      { linkId: 'email', answer: [{ valueString: identity.email }] },
      { linkId: 'phone', answer: [{ valueString: identity.phone }] },
      { linkId: 'date-of-birth', answer: [{ valueDate: identity.dateOfBirth }] },
      {
        linkId: 'sex-at-birth',
        answer: [
          { valueCoding: { system: 'http://hl7.org/fhir/administrative-gender', code: 'male', display: 'Male' } },
        ],
      },
      {
        linkId: 'gender-identity',
        answer: [
          { valueCoding: { system: 'http://hl7.org/fhir/administrative-gender', code: 'male', display: 'Male' } },
        ],
      },
      // Insurance branch (vs self-pay): the prod bot deploy predates the
      // self-pay feature in this repo, so `self-pay: true` falls through to
      // the insurance check and is rejected. Use a real supported insurance
      // provider + a fake member id instead. Provider strings must match
      // SUPPORTED_CAREFIRST_ORGS in apps/provider-app/bots/services/intake-eligibility/constants.ts.
      {
        linkId: 'insurance-provider',
        answer: [{ valueString: 'CareFirst Blue Cross Blue Shield Maryland' }],
      },
      { linkId: 'member-id', answer: [{ valueString: `ZOOMTEST${identity.timestamp}` }] },
      { linkId: 'street-address', answer: [{ valueString: '123 Test St' }] },
      { linkId: 'city', answer: [{ valueString: 'Bethesda' }] },
      {
        linkId: 'state',
        answer: [{ valueCoding: { system: 'https://www.usps.com/', code: 'MD', display: 'Maryland' } }],
      },
      { linkId: 'zip-code', answer: [{ valueString: '20814' }] },
      { linkId: 'consent-to-contact', answer: [{ valueBoolean: true }] },
    ],
  };
}
